/**
 * SEO form-effect — writes <meta> / <link> / <title> tags into document.head.
 * Follows the FormEffectHandler contract: returns a cleanup function.
 * Only runs in a browser context (SSR-safe guard on document).
 */

import type { FormEffectHandler } from '@atlas-forms/types-js';
import { str } from './utils';

/** Set or create a <meta name="..."> tag. Returns the element for cleanup. */
function setMetaName(name: string, content: string): HTMLMetaElement | null {
  if (typeof document === 'undefined') return null;
  if (!content) return null;
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement('meta');
    el.name = name;
    document.head.appendChild(el);
  }
  el.content = content;
  return created ? el : null; // only return the element if WE created it
}

/** Set or create a <meta property="..."> (Open Graph) tag. */
function setMetaProperty(property: string, content: string): HTMLMetaElement | null {
  if (typeof document === 'undefined') return null;
  if (!content) return null;
  let el = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.content = content;
  return created ? el : null;
}

/** Set or create a <link rel="..."> tag. */
function setLinkRel(rel: string, href: string): HTMLLinkElement | null {
  if (typeof document === 'undefined') return null;
  if (!href) return null;
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  return created ? el : null;
}

export const seoEffect: FormEffectHandler = (config) => {
  if (typeof document === 'undefined') return;

  const created: Element[] = [];
  const origTitle = document.title;

  // <title>
  const metaTitle = str(config.metaTitle);
  if (metaTitle) document.title = metaTitle;

  // <meta name="description">
  const desc = setMetaName('description', str(config.metaDescription));
  if (desc) created.push(desc);

  // <meta name="robots">
  const robots = str(config.robotsDirective) || 'index';
  const robotsEl = setMetaName('robots', robots);
  if (robotsEl) created.push(robotsEl);

  // Open Graph
  const ogTitle = setMetaProperty('og:title', str(config.ogTitle));
  if (ogTitle) created.push(ogTitle);

  const ogDesc = setMetaProperty('og:description', str(config.ogDescription));
  if (ogDesc) created.push(ogDesc);

  const ogImage = setMetaProperty('og:image', str(config.ogImage));
  if (ogImage) created.push(ogImage);

  // Canonical
  const canonical = setLinkRel('canonical', str(config.canonicalUrl));
  if (canonical) created.push(canonical);

  // Favicon
  const favicon = setLinkRel('icon', str(config.faviconUrl));
  if (favicon) created.push(favicon);

  // Cleanup: remove elements we created, restore title
  return () => {
    if (metaTitle) document.title = origTitle;
    for (const el of created) {
      el.parentNode?.removeChild(el);
    }
  };
};
